import { Link } from "react-router-dom";
import { formatEURCompact } from "@/lib/api";
import { Coins, Lightning } from "@phosphor-icons/react";

const fr = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—");
const SIG = { blocage: "bg-red-100 text-red-800", alerte: "bg-amber-100 text-amber-800", inactivite: "bg-slate-100 text-slate-600" };
const SIG_PREFIX = { blocage: "Blocage documenté", alerte: "Alerte", inactivite: "Inactivité" };

function Paiement({ p, label, testId }) {
  return (
    <div className="rounded-2xl bg-white/10 p-3" data-testid={testId}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-emerald-200">{label} · {fr(p.date)}</div>
      <div className="font-display text-2xl font-extrabold text-white cb-pay-confirme" data-testid={`${testId}-confirme`}>{formatEURCompact(p.confirme)}</div>
      <div className="text-[11px] text-emerald-100">{p.n_confirme} signature{p.n_confirme > 1 ? "s" : ""} réelle{p.n_confirme > 1 ? "s" : ""}{p.projete > 0 ? ` · + ${formatEURCompact(p.projete)} projeté (non confirmé, ${p.n_projete})` : ""}</div>
    </div>
  );
}

export function TresorerieBlock({ data }) {
  const s = data.summary;
  return (
    <section className="cb-section rounded-3xl p-5 md:p-6" style={{ background: "linear-gradient(135deg,var(--cf-success-text) 0%,var(--cf-success-text) 100%)" }} data-testid="cb-tresorerie">
      <div className="flex items-center gap-2 mb-3">
        <Coins size={16} weight="fill" color="var(--cf-success-border)" />
        <div className="overline" style={{ color: "var(--cf-success-border)" }}>1 · Trésorerie à débloquer</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
        <Paiement p={s.prochain_paiement} label="Prochain paiement" testId="cb-pay-next" />
        <Paiement p={s.paiement_suivant} label="Paiement suivant" testId="cb-pay-after" />
        <div className="rounded-2xl bg-white/10 p-3" data-testid="cb-encaisses">
          <div className="text-[11px] font-bold uppercase tracking-wide text-emerald-200">Encaissé ce mois (constaté)</div>
          <div className="font-display text-2xl font-extrabold text-white" data-testid="cb-encaisses-value">{formatEURCompact(s.encaisses_mois.ca_net)}</div>
          <div className="text-[11px] text-emerald-100">{s.encaisses_mois.count} dossier{s.encaisses_mois.count > 1 ? "s" : ""} · date d'encaissement enregistrée</div>
        </div>
      </div>
      {data.top.length === 0 ? (
        <div className="rounded-2xl bg-white/10 p-4 text-sm text-emerald-50" data-testid="cb-treso-empty">Aucune opportunité de trésorerie actionnable aujourd'hui (aucun dossier en phase finale non encaissé).</div>
      ) : (
        <div className="space-y-2">
          {data.top.map((t, i) => (
            <div key={t.dossier_id} className="rounded-2xl bg-white p-4" data-testid={`cb-treso-item-${i}`}>
              <div className="flex flex-wrap items-start gap-2">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-base font-extrabold text-slate-900" data-testid={`cb-treso-nom-${i}`}>{t.nom}</span>
                    <span className="text-[11px] font-bold text-slate-500 uppercase">{t.etape?.replace(/_/g, " ")}</span>
                    {t.accelere_prochaine_echeance && <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5" data-testid={`cb-treso-accel-${i}`}><Lightning size={11} weight="fill" /> Peut accélérer la prochaine échéance</span>}
                  </div>
                  <div className="text-xs text-slate-600 mt-1" data-testid={`cb-treso-motif-${i}`}>{t.motif}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`cb-treso-proj-${i}`}>{t.projection.label}</div>
                  {t.signaux.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">{t.signaux.map((sg) => <span key={sg.code} className={`text-[10px] font-bold rounded-md px-1.5 py-0.5 ${SIG[sg.kind]}`} data-testid={`cb-treso-signal-${sg.kind}`}>{SIG_PREFIX[sg.kind]} : {sg.label}</span>)}</div>
                  )}
                  <div className="text-sm font-bold text-slate-900 mt-2" data-testid={`cb-treso-action-${i}`}>→ {t.action}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl font-extrabold text-emerald-700" data-testid={`cb-treso-ca-${i}`}>{formatEURCompact(t.ca_net)}</div>
                  <div className="text-[10px] text-slate-400">CA net attendu</div>
                  <Link to={`/dossier/${t.dossier_id}`} className="inline-block mt-2 text-xs font-bold text-white bg-slate-900 rounded-lg px-3 py-1.5" data-testid={`cb-treso-open-${i}`}>Ouvrir le dossier</Link>
                </div>
              </div>
            </div>
          ))}
          {data.autres_count > 0 && <div className="text-[11px] text-emerald-100 px-1" data-testid="cb-treso-more">+ {data.autres_count} autre{data.autres_count > 1 ? "s" : ""} dossier{data.autres_count > 1 ? "s" : ""} en phase finale non encaissé{data.autres_count > 1 ? "s" : ""}</div>}
        </div>
      )}
      <p className="text-[10px] text-emerald-200/80 mt-3" data-testid="cb-treso-note">{s.note}</p>
    </section>
  );
}
